(() => {
  if (customElements.get('reise-media-viewer')) return;

  class ReiseMediaViewer extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.dialog = this.querySelector('[data-rp-dialog="zoom"]');
      this.stage = this.querySelector('[data-rpv-stage]');
      this.content = this.querySelector('[data-rpv-content]');
      this.items = [...this.querySelectorAll('template[data-rpv-item]')];
      this.thumbs = [...this.querySelectorAll('[data-rpv-thumb]')];
      this.pointers = new Map();
      this.zoom = 1;
      this.x = this.y = 0;
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.dialog || !this.stage || !this.content) return;

      this.addEventListener('click', event => this.onClick(event), { signal });
      this.dialog.addEventListener('keydown', event => this.onKeydown(event), { signal });
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.onClose();
      }, { signal });
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, { signal });
      this.motionPreference.addEventListener('change', () => {
        if (!this.motionPreference.matches) return;
        this.dialogAnimation?.finish();
        this.zoomAnimation?.finish();
        this.mediaAnimation?.finish();
      }, { signal });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }, { signal });
      this.stage.addEventListener('wheel', event => {
        if (!this.imageReady) return;
        event.preventDefault();
        this.stopZoomAnimation();
        const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.stage.clientHeight : 1);
        this.setZoom(this.zoom * Math.exp(-delta * 0.002), this.point(event), false);
      }, { passive: false, signal });
      this.stage.addEventListener('pointerdown', event => this.pointerDown(event), { signal });
      this.stage.addEventListener('pointermove', event => this.pointerMove(event), { signal });
      this.stage.addEventListener('pointerup', event => this.pointerEnd(event), { signal });
      this.stage.addEventListener('pointercancel', event => this.pointerEnd(event, true), { signal });
      this.stage.addEventListener('dragstart', event => {
        if (this.image) event.preventDefault();
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.fitImage());
      this.resizeObserver.observe(this.stage);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      this.onClose(false);
    }

    open(index = 0, opener = document.activeElement) {
      if (!this.items?.length) return;
      const fresh = !this.dialog.open;
      if (fresh) {
        if (this.scrollLock) this.onClose(false);
        this.opener = opener;
        this.scrollLock = {
          body: document.body.style.overflow,
          html: document.documentElement.style.overflow
        };
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        this.closest('reise-product')?.querySelectorAll('video').forEach(video => {
          if (!this.contains(video)) video.pause();
        });
        this.dialog.showModal();
      }
      if (fresh || this.isClosing) this.animateDialog(true, fresh);
      this.show(index);
      this.querySelector('[data-rpv-close]')?.focus({ preventScroll: true });
    }

    animateDialog(opening, fresh = false) {
      const styles = getComputedStyle(this.dialog);
      const from = { opacity: fresh ? 0 : styles.opacity, transform: fresh ? 'translate3d(0, 12px, 0)' : styles.transform };
      this.dialogAnimation?.cancel();
      this.dialogAnimation = null;
      this.isClosing = !opening;
      this.stage.inert = !opening;
      this.dialog.toggleAttribute('data-closing', !opening);
      if (this.motionPreference.matches) {
        if (!opening) this.dialog.close();
        return;
      }
      const animation = this.dialog.animate([
        from,
        { opacity: opening ? 1 : 0, transform: opening ? 'translate3d(0, 0, 0)' : 'translate3d(0, 8px, 0)' }
      ], { duration: opening ? 280 : 180, easing: 'cubic-bezier(.2, .7, .2, 1)', fill: 'both' });
      this.dialogAnimation = animation;
      animation.finished.then(() => {
        if (this.dialogAnimation !== animation) return;
        if (!opening) this.dialog.close();
        animation.cancel();
        this.dialogAnimation = null;
      }).catch(() => {});
    }

    close() {
      if (this.dialog.open && !this.isClosing) this.animateDialog(false);
    }

    onClose(restoreFocus = true) {
      this.dialogAnimation?.cancel();
      this.dialogAnimation = null;
      this.isClosing = false;
      this.stage.inert = false;
      this.dialog?.removeAttribute('data-closing');
      this.clearMedia();
      if (this.scrollLock) {
        document.body.style.overflow = this.scrollLock.body;
        document.documentElement.style.overflow = this.scrollLock.html;
        this.scrollLock = null;
      }
      if (restoreFocus && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }

    clearMedia() {
      this.zoomAnimation?.cancel();
      this.zoomAnimation = null;
      this.mediaAnimation?.cancel();
      this.mediaAnimation = null;
      this.mediaController?.abort();
      this.content?.querySelectorAll('video').forEach(video => {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        video.load();
      });
      this.content?.querySelectorAll('iframe').forEach(frame => { frame.src = 'about:blank'; });
      this.content?.replaceChildren();
      this.pointers?.clear();
      this.gesture = null;
      this.lastTap = null;
      this.image = null;
      this.imageReady = false;
      this.stage?.classList.remove('is-loading', 'is-zoomed', 'is-dragging');
    }

    show(index) {
      if (this.isClosing) return;
      const next = ((Number(index) || 0) % this.items.length + this.items.length) % this.items.length;
      if (next === this.index && this.content.hasChildNodes()) return;
      const direction = this.index == null ? 0 : Math.sign(Number(index) - this.index);
      this.clearMedia();
      this.index = next;
      this.zoom = 1;
      this.x = this.y = 0;
      const template = this.items[this.index];
      this.type = template.dataset.type;
      this.dialog.dataset.mediaType = this.type;
      this.mediaController = new AbortController();
      const signal = this.mediaController.signal;
      const status = this.querySelector('[data-rpv-status]');
      if (status) {
        status.textContent = '';
        status.hidden = true;
      }
      this.querySelector('[data-rpv-counter]').textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.items.length).padStart(2, '0')}`;
      this.querySelectorAll('[data-rpv-step]').forEach(button => { button.disabled = this.items.length < 2; });
      this.thumbs.forEach(thumb => {
        if (Number(thumb.dataset.rpvThumb) === this.index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      const fragment = template.content.cloneNode(true);
      fragment.querySelectorAll('video').forEach(video => {
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.controls = true;
        video.playsInline = true;
        video.preload = 'metadata';
      });
      fragment.querySelectorAll('iframe').forEach(frame => {
        const url = new URL(frame.src, location.href);
        url.searchParams.set('autoplay', '0');
        frame.src = url.href;
      });
      this.content.replaceChildren(fragment);

      const fail = () => {
        this.imageReady = false;
        this.stage.classList.remove('is-loading');
        if (status) {
          status.textContent = this.dataset.errorLabel || '';
          status.hidden = false;
        }
        this.updateControls();
      };
      if (this.type === 'image') {
        const image = this.content.querySelector('img');
        this.image = image;
        if (!image) return fail();
        image.draggable = false;
        image.loading = 'eager';
        this.stage.classList.add('is-loading');
        const ready = () => {
          if (this.image !== image || !this.dialog.open) return;
          if (!image.naturalWidth) return fail();
          this.imageReady = true;
          this.stage.classList.remove('is-loading');
          this.fitImage();
          this.revealMedia(direction);
        };
        image.addEventListener('load', ready, { once: true, signal });
        image.addEventListener('error', fail, { once: true, signal });
        if (image.complete) ready();
      } else {
        this.revealMedia(direction);
        this.content.querySelectorAll('video').forEach(video => {
          video.addEventListener('error', fail, { once: true, signal });
        });
      }
      this.updateControls();
      this.revealThumb();
    }

    revealMedia(direction) {
      this.mediaAnimation?.cancel();
      this.mediaAnimation = null;
      if (this.motionPreference.matches || this.isClosing) return;
      const animation = this.content.animate([
        { opacity: 0, transform: `translate3d(${direction * 20}px, 0, 0)` },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' }
      ], { duration: 220, easing: 'cubic-bezier(.2, .7, .2, 1)', fill: 'both' });
      this.mediaAnimation = animation;
      animation.finished.then(() => {
        if (this.mediaAnimation !== animation) return;
        animation.cancel();
        this.mediaAnimation = null;
      }).catch(() => {});
    }

    revealThumb() {
      const thumb = this.thumbs.find(button => Number(button.dataset.rpvThumb) === this.index);
      const rail = thumb?.closest('[data-rpv-thumbs]') || thumb?.parentElement;
      if (!rail || !thumb) return;
      const item = thumb.getBoundingClientRect();
      const bounds = rail.getBoundingClientRect();
      const left = item.left < bounds.left ? item.left - bounds.left : item.right > bounds.right ? item.right - bounds.right : 0;
      const top = item.top < bounds.top ? item.top - bounds.top : item.bottom > bounds.bottom ? item.bottom - bounds.bottom : 0;
      rail.scrollBy({ left, top, behavior: 'instant' });
    }

    fitImage() {
      if (!this.imageReady || !this.dialog.open) return;
      this.stopZoomAnimation();
      const rect = this.stage.getBoundingClientRect();
      const ratio = Math.min(rect.width / this.image.naturalWidth, rect.height / this.image.naturalHeight);
      this.fitWidth = this.image.naturalWidth * ratio;
      this.fitHeight = this.image.naturalHeight * ratio;
      this.image.style.width = `${this.fitWidth}px`;
      this.image.style.height = `${this.fitHeight}px`;
      this.renderTransform();
    }

    point(event) {
      const rect = this.stage.getBoundingClientRect();
      return { x: event.clientX - rect.left - rect.width / 2, y: event.clientY - rect.top - rect.height / 2 };
    }

    stopZoomAnimation() {
      if (!this.zoomAnimation || !this.image) return;
      const matrix = new DOMMatrixReadOnly(getComputedStyle(this.image).transform);
      this.zoomAnimation.cancel();
      this.zoomAnimation = null;
      this.zoom = matrix.a;
      this.x = matrix.e + this.fitWidth / 2;
      this.y = matrix.f + this.fitHeight / 2;
      this.renderTransform();
    }

    setZoom(value, anchor = { x: 0, y: 0 }, animate = true) {
      if (!this.imageReady) return;
      this.stopZoomAnimation();
      const next = Math.min(4, Math.max(1, value));
      const ratio = next / this.zoom;
      this.x = anchor.x - (anchor.x - this.x) * ratio;
      this.y = anchor.y - (anchor.y - this.y) * ratio;
      this.zoom = next;
      this.renderTransform(animate);
    }

    renderTransform(animate = false) {
      if (!this.imageReady) return;
      const from = animate ? getComputedStyle(this.image).transform : null;
      const maxX = Math.max(0, (this.fitWidth * this.zoom - this.stage.clientWidth) / 2);
      const maxY = Math.max(0, (this.fitHeight * this.zoom - this.stage.clientHeight) / 2);
      this.x = Math.min(maxX, Math.max(-maxX, this.x));
      this.y = Math.min(maxY, Math.max(-maxY, this.y));
      this.image.style.transform = `translate3d(-50%, -50%, 0) translate3d(${this.x}px, ${this.y}px, 0) scale(${this.zoom})`;
      if (animate && !this.motionPreference.matches) {
        const animation = this.image.animate([{ transform: from }, { transform: this.image.style.transform }], {
          duration: 240, easing: 'cubic-bezier(.2, .7, .2, 1)', fill: 'both'
        });
        this.zoomAnimation = animation;
        animation.finished.then(() => {
          if (this.zoomAnimation !== animation) return;
          animation.cancel();
          this.zoomAnimation = null;
        }).catch(() => {});
      }
      this.stage.classList.toggle('is-zoomed', this.zoom > 1.01);
      this.updateControls();
    }

    updateControls() {
      const isImage = this.type === 'image';
      const tools = this.querySelector('[data-rpv-tools]');
      if (!isImage && tools?.contains(document.activeElement)) this.stage.focus({ preventScroll: true });
      if (tools) tools.hidden = !isImage;
      const scale = this.querySelector('[data-rpv-scale]');
      if (scale) scale.textContent = `${this.zoom.toLocaleString(document.documentElement.lang || 'pt-BR', { maximumFractionDigits: 1 })}×`;
      this.querySelectorAll('[data-rpv-zoom]').forEach(button => {
        const disabled = !this.imageReady || (Number(button.dataset.rpvZoom) > 0 ? this.zoom >= 4 : this.zoom <= 1);
        if (disabled && button === document.activeElement) this.stage.focus({ preventScroll: true });
        button.disabled = disabled;
      });
      const reset = this.querySelector('[data-rpv-reset]');
      if (reset) {
        const disabled = !this.imageReady || this.zoom <= 1;
        if (disabled && reset === document.activeElement) this.stage.focus({ preventScroll: true });
        reset.disabled = disabled;
      }
      const hint = this.querySelector('[data-rpv-hint]');
      if (hint) hint.textContent = isImage
        ? (this.zoom > 1.01 ? this.dataset.dragHint : matchMedia('(pointer: coarse)').matches && this.dataset.touchHint ? this.dataset.touchHint : this.dataset.zoomHint) || ''
        : (this.type.includes('video') ? this.dataset.videoLabel : '') || '';
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-rpv-close')) this.close();
      else if (button.hasAttribute('data-rpv-thumb')) this.show(button.dataset.rpvThumb);
      else if (button.hasAttribute('data-rpv-step')) this.show(this.index + Number(button.dataset.rpvStep));
      else if (button.hasAttribute('data-rpv-zoom')) this.setZoom(this.zoom + Number(button.dataset.rpvZoom) * 0.5);
      else if (button.hasAttribute('data-rpv-reset')) this.setZoom(1);
    }

    onKeydown(event) {
      if (event.target.closest('video, iframe, model-viewer, input, textarea, select, [contenteditable="true"]')) return;
      if (event.shiftKey && this.imageReady && this.zoom > 1 && event.key.startsWith('Arrow')) {
        event.preventDefault();
        this.stopZoomAnimation();
        this.x += event.key === 'ArrowLeft' ? 48 : event.key === 'ArrowRight' ? -48 : 0;
        this.y += event.key === 'ArrowUp' ? 48 : event.key === 'ArrowDown' ? -48 : 0;
        this.renderTransform();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      } else if (this.imageReady && ['+', '=', '-', '_', '0'].includes(event.key)) {
        event.preventDefault();
        this.setZoom(event.key === '0' ? 1 : this.zoom + (event.key === '-' || event.key === '_' ? -0.5 : 0.5));
      }
    }

    pointerDown(event) {
      if (!this.imageReady || this.isClosing || event.button !== 0 || event.target.closest('button') || this.pointers.size >= 2) return;
      event.preventDefault();
      this.stopZoomAnimation();
      this.mediaAnimation?.finish();
      this.stage.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.gesture = {
          type: 'pinch', distance: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
          center: this.point({ clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 }),
          zoom: this.zoom, x: this.x, y: this.y
        };
        this.lastTap = null;
      } else {
        this.gesture = { type: 'pan', startX: event.clientX, startY: event.clientY, x: this.x, y: this.y, moved: false, hadPinch: false };
      }
    }

    pointerMove(event) {
      if (!this.pointers.has(event.pointerId) || !this.gesture) return;
      event.preventDefault();
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const gesture = this.gesture;
      if (gesture.type === 'pinch' && this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        const center = this.point({ clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 });
        this.zoom = Math.min(4, Math.max(1, gesture.zoom * Math.hypot(b.x - a.x, b.y - a.y) / gesture.distance));
        this.x = center.x - (gesture.center.x - gesture.x) * this.zoom / gesture.zoom;
        this.y = center.y - (gesture.center.y - gesture.y) * this.zoom / gesture.zoom;
        this.renderTransform();
      } else if (gesture.type === 'pan') {
        const dx = event.clientX - gesture.startX;
        const dy = event.clientY - gesture.startY;
        gesture.moved ||= Math.hypot(dx, dy) > 5;
        if (this.zoom > 1) {
          this.x = gesture.x + dx;
          this.y = gesture.y + dy;
          this.stage.classList.toggle('is-dragging', gesture.moved);
          this.renderTransform();
        }
      }
    }

    pointerEnd(event, cancelled = false) {
      if (!this.pointers.has(event.pointerId)) return;
      const gesture = this.gesture;
      this.pointers.delete(event.pointerId);
      if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
      this.stage.classList.remove('is-dragging');
      if (this.pointers.size) {
        const point = this.pointers.values().next().value;
        this.gesture = { type: 'pan', startX: point.x, startY: point.y, x: this.x, y: this.y, moved: true, hadPinch: true };
        return;
      }
      this.gesture = null;
      if (cancelled || !gesture || gesture.type === 'pinch' || gesture.hadPinch) return;
      const dx = event.clientX - gesture.startX;
      const dy = event.clientY - gesture.startY;
      if (this.zoom <= 1.01 && Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.25) {
        this.show(this.index + (dx < 0 ? 1 : -1));
      } else if (!gesture.moved && Math.hypot(dx, dy) < 6) {
        if (event.pointerType === 'mouse') this.setZoom(this.zoom > 1.01 ? 1 : 2.5, this.point(event));
        else {
          const now = performance.now();
          if (this.lastTap && now - this.lastTap.time < 320 && Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < 30) {
            this.setZoom(this.zoom > 1.01 ? 1 : 2.5, this.point(event));
            this.lastTap = null;
          } else this.lastTap = { time: now, x: event.clientX, y: event.clientY };
        }
      }
    }
  }

  customElements.define('reise-media-viewer', ReiseMediaViewer);
})();
